import React, { useCallback, useEffect, useMemo } from 'react';

import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import {
	type EnablementTouchpoint,
	EnablementTouchpointEnum,
} from '@atlassian/expansion-services/services';

import { createPlatformSiteAri } from '@townsquare/collab-context/create-site-ari';
import { useFlagActions } from '@townsquare/flags';
import { useModalActions } from '@townsquare/modals';
import { noop } from '@townsquare/noop';
import { expVal } from '@townsquare/stat-sig';
import { JIRA_SPACE_INVITE_TEAMS_APP } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';

import InvitePeople from './components/AkInvitePeople';
import { useOnSubmit } from './hooks/useOnSubmit';
import { useSpaceInviteInput } from './hooks/useSpaceInviteInput';
import { useInviteUserStore } from './store';

export const useInviteUserToSiteModal = ({
	cloudId,
	analyticsSource,
	enabled = true,
}: {
	cloudId: string | null | undefined;
	analyticsSource: string;
	/**
	 * Defaults to true
	 */
	enabled?: boolean;
}): {
	openInviteUserToSiteModal: () => void;
	canInviteUserToSite: boolean;
} => {
	const { openModal, closeModal } = useModalActions();
	const [{ canInviteUserToSite }, { checkAccessToInviteUserToSite }] = useInviteUserStore();

	useEffect(() => {
		if (!cloudId || !enabled) {
			return;
		}
		checkAccessToInviteUserToSite(cloudId);
	}, [checkAccessToInviteUserToSite, cloudId, enabled]);

	const handleOpen = useCallback((): void => {
		const touchpoint = fg('people-directory-touchpoint')
			? EnablementTouchpointEnum.PEOPLE_DIRECTORY
			: undefined;
		openModal('invite-user-to-site-modal', {
			component: expVal(JIRA_SPACE_INVITE_TEAMS_APP, 'isEnabled', false) ? (
				<InviteToSiteModalWithSpaceInviteExperiment
					analyticsSource={analyticsSource}
					cloudId={cloudId}
					onCancel={() => closeModal('invite-user-to-site-modal')}
					touchpoint={touchpoint}
				/>
			) : (
				<InviteToSiteModal
					analyticsSource={analyticsSource}
					cloudId={cloudId}
					onCancel={() => closeModal('invite-user-to-site-modal')}
					touchpoint={touchpoint}
				/>
			),
		});
	}, [openModal, analyticsSource, cloudId, closeModal]);

	return {
		openInviteUserToSiteModal: canInviteUserToSite ? handleOpen : noop,
		canInviteUserToSite,
	};
};

type Props = {
	onCancel: () => void;
	cloudId: string | null | undefined;
	analyticsSource: string;
	touchpoint?: EnablementTouchpoint;
};

const InviteToSiteModal = ({ onCancel, cloudId, analyticsSource, touchpoint }: Props) => {
	const { addFlag } = useFlagActions();

	const onClose = () => {
		onCancel();
	};

	if (!cloudId) {
		return null;
	}

	return (
		<ModalDialog onClose={onClose} width="small">
			<ModalBody>
				<InvitePeople
					resourceAri={`ari:cloud:platform::site/${cloudId}`}
					onCancelHandler={onClose}
					showFlag={(flag) => addFlag(flag)}
					analyticsContext={{
						source: analyticsSource,
					}}
					onSendHandler={onClose}
					source={analyticsSource}
					touchpoint={touchpoint}
				/>
			</ModalBody>
		</ModalDialog>
	);
};

const InviteToSiteModalWithSpaceInviteExperiment = ({
	onCancel,
	cloudId,
	analyticsSource,
	touchpoint,
}: Props) => {
	const { addFlag } = useFlagActions();
	const spaceInviteInput = useSpaceInviteInput({ cloudId });
	const onSubmit = useOnSubmit(
		spaceInviteInput.data.selectedSpace,
		spaceInviteInput.data.selectedRole,
		cloudId,
		onCancel,
	);

	const plugins = useMemo(
		() => ({
			footer: spaceInviteInput.plugin,
		}),
		[spaceInviteInput.plugin],
	);

	if (!cloudId) {
		return null;
	}

	return (
		<ModalDialog onClose={onCancel} width="small">
			<ModalBody>
				<InvitePeople
					resourceAri={createPlatformSiteAri(cloudId) ?? ''}
					onCancelHandler={onCancel}
					showFlag={(flag) => addFlag(flag)}
					analyticsContext={{
						source: analyticsSource,
					}}
					onSendHandler={onCancel}
					source={analyticsSource}
					plugins={spaceInviteInput.plugin ? plugins : undefined}
					onSubmit={onSubmit}
					touchpoint={touchpoint}
				/>
			</ModalBody>
		</ModalDialog>
	);
};
